import axios from "axios";
import React, { useEffect, useState } from "react";
import {
  FiCreditCard,
  FiDollarSign,
  FiClock,
  FiArrowUp,
  FiArrowDown,
  FiRefreshCw,
  FiX,
  FiPlus,
  FiMinus,
  FiInfo,
  FiClipboard
} from "react-icons/fi";
import CountUp from "react-countup";
import "./EmployeeDashboard.css";

const API_BASE_URL = 'https://api.ingrainsystems.com/api';

const MyWallet = () => {
  const [walletData, setWalletData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [showRechargeModal, setShowRechargeModal] = useState(false);
  const [rechargeCoins, setRechargeCoins] = useState('');
  const [rechargeLoading, setRechargeLoading] = useState(false);
  const [rechargeMessage, setRechargeMessage] = useState({ text: "", type: "" });

  const getClientId = () => {
    return localStorage.getItem('clientId') || '';
  };

  useEffect(() => {
    fetchWalletData();
    // Load Razorpay script
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const fetchWalletData = async () => {
    try {
      setLoading(true);
      const clientId = getClientId();
      
      if (!clientId) {
        setError("Client ID not found. Please login again.");
        setLoading(false);
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/clients/my-wallet/${clientId}`);
      
      console.log("Wallet API Response:", response.data);
      
      if (response.data.success) {
        setWalletData(response.data.data);
      } else {
        setError(response.data.message || "Failed to fetch wallet data");
      }
    } catch (err) {
      console.error("Error fetching wallet:", err);
      setError(err.response?.data?.message || "Failed to fetch wallet data");
    } finally {
      setLoading(false);
    }
  };

  const handleRecharge = async () => {
    const coins = parseInt(rechargeCoins);
    
    if (!coins || coins < 1) {
      setRechargeMessage({ text: "Please enter valid number of coins (minimum 1)", type: "error" });
      return;
    }

    try {
      setRechargeLoading(true);
      setRechargeMessage({ text: "", type: "" });
      
      const clientId = getClientId();
      
      // Create Razorpay order
      const orderResponse = await axios.post(`${API_BASE_URL}/clients/recharge-wallet/${clientId}`, {
        coins: coins
      });
      
      if (orderResponse.data.success) {
        const options = {
          key: orderResponse.data.data.razorpayKey,
          amount: orderResponse.data.data.amount,
          currency: orderResponse.data.data.currency,
          name: "Timely Health Care",
          description: `Recharge Wallet with ${coins} Coins`,
          order_id: orderResponse.data.data.orderId,
          handler: async (response) => {
            // Verify payment
            try {
              const verifyResponse = await axios.post(`${API_BASE_URL}/clients/verify-wallet-recharge/${clientId}`, {
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature
              });
              
              if (verifyResponse.data.success) {
                setRechargeMessage({ text: "Wallet recharged successfully!", type: "success" });
                setTimeout(() => {
                  setShowRechargeModal(false);
                  setRechargeCoins('');
                  setRechargeMessage({ text: "", type: "" });
                  fetchWalletData(); // Refresh wallet data
                }, 2000);
              } else {
                setRechargeMessage({ text: verifyResponse.data.message || "Payment verification failed", type: "error" });
              }
            } catch (err) {
              console.error("Verification error:", err);
              setRechargeMessage({ text: err.response?.data?.message || "Payment verification failed", type: "error" });
            }
          },
          prefill: {
            email: localStorage.getItem('clientEmail'),
            name: localStorage.getItem('clientName')
          },
          notes: {
            clientId: clientId,
            coins: coins
          },
          theme: {
            color: "#3B82F6"
          },
          modal: {
            ondismiss: () => {
              setRechargeLoading(false);
              setRechargeMessage({ text: "Payment cancelled", type: "error" });
            }
          }
        };
        
        const razorpayInstance = new window.Razorpay(options);
        razorpayInstance.open();
      } else {
        setRechargeMessage({ text: orderResponse.data.message || "Failed to create order", type: "error" });
      }
    } catch (err) {
      console.error("Recharge error:", err);
      setRechargeMessage({ text: err.response?.data?.message || "Failed to create order", type: "error" });
    } finally {
      setRechargeLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const getFilteredTransactions = () => {
    if (!walletData?.walletHistory) return [];
    if (selectedFilter === 'all') return walletData.walletHistory;
    return walletData.walletHistory.filter(t => t.type === selectedFilter);
  };

  if (loading) {
    return (
      <div className="emp-dash">
        <main className="p-4 sm:p-6 lg:p-8">
          <div className="py-10 text-center">
            <div className="flex flex-col items-center justify-center gap-3">
              <div className="emp-dash__spinner"></div>
              <span className="text-sm font-medium text-gray-500">Syncing wallet data...</span>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="emp-dash">
        <main className="p-4 sm:p-6 lg:p-8">
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
            <FiX className="text-red-500 text-lg" />
            <span className="text-sm font-medium text-red-700">{error}</span>
          </div>
          <div className="text-center">
            <button
              onClick={fetchWalletData}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition"
            >
              Try Again
            </button>
          </div>
        </main>
      </div>
    );
  }

  if (!walletData) {
    return (
      <div className="emp-dash">
        <main className="p-4 sm:p-6 lg:p-8">
          <div className="text-center py-20">
            <div className="bg-gray-50 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <FiCreditCard className="text-2xl text-gray-200" />
            </div>
            <h2 className="text-lg font-bold text-gray-800">No wallet data found</h2>
            <p className="text-xs text-gray-400 mt-1">Please try again later</p>
          </div>
        </main>
      </div>
    );
  }

  const filteredTransactions = getFilteredTransactions();
  const totalCredited = walletData.walletHistory?.filter(t => t.type === 'credit').reduce((sum, t) => sum + t.coins, 0) || 0;
  const totalDebited = walletData.walletHistory?.filter(t => t.type === 'debit').reduce((sum, t) => sum + t.coins, 0) || 0;

  // Get total coins from response - either from totalCoins or wallet.coins
  const totalCoins = walletData.totalCoins || walletData.wallet?.coins || 0;

  return (
    <div className="emp-dash">
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Dashboard Header */}
        <div className="emp-dash__header">
          <div>
            <h1 className="emp-dash__greeting">
              My <span>Wallet</span>
            </h1>
            <p className="emp-dash__subtitle">
              Manage your wallet balance and transactions
            </p>
          </div>
          <div className="emp-dash__date-pill">
            <FiClipboard />
            <span>
              {new Date().toLocaleDateString("en-US", {
                weekday: "short",
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </span>
          </div>
        </div>

        {/* Balance Card with Recharge Button */}
        {/* <div className="emp-dash__card mb-6">
          <div className="p-6 md:p-8 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-3xl relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full blur-2xl -ml-24 -mb-24 pointer-events-none"></div>
            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-4 md:gap-5">
                <div className="w-16 h-16 md:w-20 md:h-20 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center shadow-xl border border-white/30">
                  <FiCreditCard className="text-white text-2xl md:text-3xl" />
                </div>
                <div>
                  <p className="text-[10px] md:text-xs font-bold text-blue-100 uppercase tracking-[0.2em] mb-1">Total Balance</p>
                  <div className="flex items-baseline gap-2">
                    <FiDollarSign className="text-yellow-300 text-xl md:text-2xl" />
                    <span className="text-3xl md:text-4xl font-black text-white">
                      <CountUp end={totalCoins} duration={2} />
                    </span>
                    <span className="text-sm md:text-base text-blue-200 font-bold">coins</span>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowRechargeModal(true)}
                className="bg-white text-blue-600 px-5 md:px-6 py-2.5 md:py-3 rounded-xl text-xs md:text-sm font-bold hover:bg-blue-50 transition-all shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 flex items-center gap-2"
              >
                <FiDollarSign className="text-sm" />
                Recharge
              </button>
            </div>
          </div>
        </div> */}

        {/* Stats Row */}
        <div className="emp-dash__stats grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="emp-dash__stat">
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Total Coins</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--indigo">
                <FiDollarSign />
              </div>
            </div>
            <div className="emp-dash__stat-value">
              <CountUp end={totalCoins} duration={2} />
            </div>
            <div className="emp-dash__stat-meta">available balance</div>
          </div>
          
          <div className="emp-dash__stat">
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Transactions</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--violet">
                <FiRefreshCw />
              </div>
            </div>
            <div className="emp-dash__stat-value">
              <CountUp end={walletData.totalTransactions || walletData.walletHistory?.length || 0} duration={2} />
            </div>
            <div className="emp-dash__stat-meta">total transactions</div>
          </div>
          
          <div className="emp-dash__stat">
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Credited</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--emerald">
                <FiArrowUp />
              </div>
            </div>
            <div className="emp-dash__stat-value text-emerald-600">
              +<CountUp end={totalCredited} duration={2} />
            </div>
            <div className="emp-dash__stat-meta">total credited</div>
          </div>
          
          <div className="emp-dash__stat">
            <div className="emp-dash__stat-top">
              <span className="emp-dash__stat-label">Debited</span>
              <div className="emp-dash__stat-icon emp-dash__stat-icon--rose">
                <FiArrowDown />
              </div>
            </div>
            <div className="emp-dash__stat-value text-red-600">
              -<CountUp end={totalDebited} duration={2} />
            </div>
            <div className="emp-dash__stat-meta">total debited</div>
          </div>
        </div>

        {/* Transaction Section */}
        <div className="emp-dash__card mb-6">
          <div className="emp-dash__card-header">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-100 text-blue-600 flex items-center justify-center shadow-inner border border-blue-200/50">
                <FiRefreshCw size={24} />
              </div>
              <div>
                <h3 className="emp-dash__card-title">Transaction History</h3>
                <p className="emp-dash__card-desc">{filteredTransactions.length} transactions</p>
              </div>
            </div>
            <div className="flex gap-1.5">
              <button
                onClick={() => setSelectedFilter('all')}
                className={`px-3 py-1.5 rounded-lg text-[10px] font-bold transition-all ${
                  selectedFilter === 'all'
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                All
              </button>
              <button
                onClick={() => setSelectedFilter('credit')}
                className={`px-3 py-1.5 rounded-lg text-[10px] font-bold transition-all flex items-center gap-1 ${
                  selectedFilter === 'credit'
                    ? 'bg-emerald-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <FiArrowUp className="text-[8px]" /> Credits
              </button>
              <button
                onClick={() => setSelectedFilter('debit')}
                className={`px-3 py-1.5 rounded-lg text-[10px] font-bold transition-all flex items-center gap-1 ${
                  selectedFilter === 'debit'
                    ? 'bg-red-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <FiArrowDown className="text-[8px]" /> Debits
              </button>
            </div>
          </div>

          <div className="emp-dash__card-body">
            {filteredTransactions.length > 0 ? (
              <div className="space-y-2 max-h-[400px] overflow-y-auto custom-scrollbar">
                {filteredTransactions.map((transaction, index) => (
                  <div
                    key={index}
                    className={`flex items-center justify-between p-3 rounded-xl transition-all ${
                      transaction.type === 'credit' 
                        ? 'bg-emerald-50/50 border border-emerald-100 hover:bg-emerald-50' 
                        : 'bg-red-50/50 border border-red-100 hover:bg-red-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-sm ${
                        transaction.type === 'credit' 
                          ? 'bg-emerald-500' 
                          : 'bg-red-500'
                      }`}>
                        {transaction.type === 'credit' ? (
                          <FiPlus className="text-white text-sm" />
                        ) : (
                          <FiMinus className="text-white text-sm" />
                        )}
                      </div>
                      <div>
                        <p className="text-xs font-bold text-gray-800">{transaction.message}</p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <FiClock className="text-[8px] text-gray-400" />
                          <p className="text-[9px] text-gray-500">{formatDateTime(transaction.createdAt)}</p>
                          <span className="w-0.5 h-0.5 rounded-full bg-gray-300"></span>
                          <p className="text-[9px] text-gray-400">{formatTimeAgo(transaction.createdAt)}</p>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-bold ${
                        transaction.type === 'credit' ? 'text-emerald-600' : 'text-red-600'
                      }`}>
                        {transaction.type === 'credit' ? '+' : '-'} {transaction.coins}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-10">
                <div className="w-16 h-16 rounded-2xl bg-gray-50 flex items-center justify-center mx-auto mb-3">
                  <FiRefreshCw className="text-2xl text-gray-300" />
                </div>
                <p className="text-xs font-bold text-gray-500">No transactions found</p>
              </div>
            )}

            {/* Summary */}
            {filteredTransactions.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center p-3 bg-emerald-50 rounded-xl border border-emerald-100">
                    <p className="text-[9px] font-bold text-emerald-600 uppercase tracking-widest">Credited</p>
                    <p className="text-sm font-bold text-emerald-700">
                      +{filteredTransactions.filter(t => t.type === 'credit').reduce((sum, t) => sum + t.coins, 0)}
                    </p>
                  </div>
                  <div className="text-center p-3 bg-red-50 rounded-xl border border-red-100">
                    <p className="text-[9px] font-bold text-red-600 uppercase tracking-widest">Debited</p>
                    <p className="text-sm font-bold text-red-700">
                      -{filteredTransactions.filter(t => t.type === 'debit').reduce((sum, t) => sum + t.coins, 0)}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Info Card */}
        <div className="emp-dash__card mb-6">
          <div className="emp-dash__card-header">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-100 text-blue-600 flex items-center justify-center shadow-inner border border-blue-200/50">
                <FiInfo size={24} />
              </div>
              <div>
                <h3 className="emp-dash__card-title">About Wallet Coins</h3>
                <p className="emp-dash__card-desc">Wallet information & usage</p>
              </div>
            </div>
          </div>
          <div className="emp-dash__card-body bg-gray-50/50">
            <p className="text-xs font-bold text-gray-800">1 Coin = ₹1 • Never expires • Use for purchases</p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-center gap-2 text-[9px] font-bold text-gray-400 uppercase tracking-widest py-4">
          <FiClipboard className="text-indigo-400" />
          Last Updated: {formatDateTime(walletData.updatedAt)}
        </div>
      </div>

      
      {showRechargeModal && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
          <div className="bg-white w-full max-w-md rounded-3xl shadow-2xl overflow-hidden relative border border-gray-100">
            
            {/* Header */}
            <div className="px-8 pt-8 pb-4 bg-gradient-to-r from-blue-50 to-indigo-50">
              <button
                onClick={() => {
                  setShowRechargeModal(false);
                  setRechargeCoins('');
                  setRechargeMessage({ text: "", type: "" });
                }}
                className="absolute top-4 right-4 w-10 h-10 rounded-xl bg-white/50 flex items-center justify-center text-gray-400 hover:text-gray-800 hover:bg-white transition-all shadow-sm"
              >
                <FiX className="text-lg" />
              </button>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center shadow-lg">
                  <FiDollarSign size={20} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Recharge Wallet</h2>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Add coins to your wallet</p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="p-8 pt-4">
              <div className="mb-4">
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1 mb-2 block">Enter Coins (1 Coin = ₹1)</label>
                <div className="relative">
                  <FiDollarSign className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                  <input
                    type="number"
                    value={rechargeCoins}
                    onChange={(e) => setRechargeCoins(e.target.value)}
                    placeholder="Enter number of coins"
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm font-bold focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all bg-gray-50/50 focus:bg-white"
                    min="1"
                  />
                </div>
                <p className="text-[10px] text-gray-500 mt-2 flex items-center gap-2">
                  <FiInfo className="text-blue-400" />
                  Amount payable: <span className="font-bold text-gray-900">₹{rechargeCoins || 0}</span>
                </p>
              </div>

              {rechargeMessage.text && (
                <div className={`mb-4 p-3 rounded-xl text-xs font-bold flex items-center gap-2 ${
                  rechargeMessage.type === 'success' ? 'bg-emerald-50 text-emerald-600 border border-emerald-200' : 'bg-red-50 text-red-600 border border-red-200'
                }`}>
                  {rechargeMessage.type === 'success' ? <FiDollarSign /> : <FiX />}
                  {rechargeMessage.text}
                </div>
              )}

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowRechargeModal(false);
                    setRechargeCoins('');
                    setRechargeMessage({ text: "", type: "" });
                  }}
                  className="flex-1 px-4 py-3 border border-gray-200 rounded-xl text-sm font-bold hover:bg-gray-50 transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRecharge}
                  disabled={rechargeLoading || !rechargeCoins}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl text-sm font-bold hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg shadow-blue-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {rechargeLoading ? 'Processing...' : 'Proceed to Pay'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* </main> */}
    </div>
  );
};

export default MyWallet;